/**
 * Spec 038 P10: what the Chart tab says when a run has no chart to show.
 * `hasChart` is the server's `has_chart`; the status only chooses the wording.
 */
export function chartEmptyText(status: string, hasChart: boolean): string | null {
  if (hasChart) return null
  switch (status) {
    case 'running':
    case 'canceling':
      return 'The chart is written when the run finishes.'
    case 'completed':
      return 'This strategy does not write a chart.'
    case 'canceled':
      return 'This run was canceled and has no chart.'
    case 'failed':
      return 'This run failed and has no chart.'
    case 'orphaned':
      return 'This run has no chart: its container disappeared.'
    default:
      return 'This run has no chart.'
  }
}

/**
 * Spec 038 P29 (7.4, 20.6 rule 4): the Chart tab has something to show when the server wrote a chart or the
 * run carries a stored equity curve (`hasStoredEquity`). Only `hasChart` makes a `chart.json` request.
 */
export function chartTabAvailable(hasChart: boolean, storedEquity: boolean): boolean {
  return hasChart || storedEquity
}

export type RunTab = 'chart' | 'log'

export interface ChartTabState {
  /** This page has shown the run as `running` at least once. */
  sawRunning: boolean
  /** `chartAvailable` as of the previous render. */
  prevAvailable: boolean
  /** The finish switch already happened for this run (once only). */
  switched: boolean
  /** The chart request has been wanted once; sticky so Chart to Log and back never refetches. */
  requested: boolean
}

export interface ChartTabInput {
  status: string
  chartAvailable: boolean
  tab: RunTab
}

/** The state a run view starts with: a run opened already running counts as seen running. */
export function initialChartTabState(input: ChartTabInput): ChartTabState {
  return {
    sawRunning: input.status === 'running',
    prevAvailable: input.chartAvailable,
    switched: false,
    requested: input.chartAvailable && input.tab === 'chart',
  }
}

/**
 * Spec 038 P11 (7.4, 7.5, decision R6): pure next state of the Chart tab logic.
 * `selectChart` is true exactly once per run, in the render where the chart
 * becomes available for a run this page saw `running`.
 */
export function nextChartTabState(
  prev: ChartTabState,
  input: ChartTabInput,
): { state: ChartTabState; selectChart: boolean } {
  const sawRunning = prev.sawRunning || input.status === 'running'
  const selectChart =
    input.chartAvailable && !prev.prevAvailable && prev.sawRunning && !prev.switched
  const switched = prev.switched || selectChart
  const requested =
    prev.requested || (input.chartAvailable && (input.tab === 'chart' || selectChart))
  return {
    state: { sawRunning, prevAvailable: input.chartAvailable, switched, requested },
    selectChart,
  }
}
